import { useDeleteMyProjectMutation } from '@/generated/graphql';
import { GET_MY_PROJECTS } from '@/queries/queries';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface UseDeleteProjectOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useDeleteProject = (options?: UseDeleteProjectOptions) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [deleteProjectMutation, { loading }] = useDeleteMyProjectMutation({
    update: (cache, { data }) => {
      const existingData = cache.readQuery({
        query: GET_MY_PROJECTS,
      }) as { myProjects: any[] };
      const newProjects = existingData?.myProjects.filter(
        (project: any) => project.id !== data?.deleteMyProject?.id
      );
      cache.writeQuery({
        query: GET_MY_PROJECTS,
        variables: {
          offset: -1
        },
        data: {
          myProjects: newProjects,
        },
      });
    },
  });

  const deleteProject = async (projectId: string) => {
    try {
      // Execute the delete mutation
      await deleteProjectMutation({
        variables: { id: projectId },
      });

      // Show success toast
      toast({
        title: t('deleteProject.success'),
        description: t('deleteProject.successDescription'),
      });

      // Call success callback if provided
      options?.onSuccess?.();
    } catch (error) {
      console.error('Failed to delete project', error);

      // Show error toast
      toast({
        title: t('deleteProject.error'),
        description: t('deleteProject.errorDescription'),
        variant: 'destructive',
      });

      // Call error callback if provided
      options?.onError?.(error as Error);
    }
  };

  return {
    deleteProject,
    loading,
  };
};
